import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { JetSportBox } from "../../components/BoxView/JetSportBox";
import { BoxHeader2 } from "../../components/BoxView/BoxHeader2";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { BoxFooter } from "../../components/BoxView/BoxFooter";
import { BoxHeaderTypography } from "../../components/Typographies/BoxHeaderTypography";
import { StrucRow } from "../../components/StructureElements/StrucRow";
import { StrucCol } from "../../components/StructureElements/StrucCol";
import { SectionHeaderTypography } from "../../components/Typographies/SectionHeaderTypography";
import { InputWrapper } from "../../components/Input/InputWrapper";
import { InputLabel } from "../../components/Input/InputLabel";
import { CustomInput } from "../../components/Input/CustomInput";
import { CustomPhoneInput } from "../../components/Input/CustomPhoneInput";
import { PrimaryButton } from "../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../components/Buttons/SecondaryButton";
import { CustomInputChangeEvent } from "../../../utils/models/events/CustomInputChangeEvent";
import { useApiClient } from "../../../data/clients/ApiClient";

enum MigrationStep {
  VALIDATION = 1,
  EMAIL_ENTRY = 2,
  SUCCESS = 3,
}

interface ValidationForm {
  username: string;
  password: string;
}

interface EmailForm {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
}

interface ValidationResponse {
  isValid: boolean;
  username: string | null;
  password: string | null;
  customerNo: string | null;
  errorMessage: string | null;
}

interface MigrationResponse {
  success: boolean;
  userId?: number;
  organizationId?: number;
  organizationName?: string;
  customerNo?: string;
  message?: string;
}

export function MigrationPage() {
  const navigate = useNavigate();
  const { validateOldCredentials, migrateFromOldWebshop } = useApiClient();
  const [currentStep, setCurrentStep] = useState<MigrationStep>(
    MigrationStep.VALIDATION
  );
  const [isLoading, setIsLoading] = useState(false);
  const [validationForm, setValidationForm] = useState<ValidationForm>({
    username: "",
    password: "",
  });
  const [emailForm, setEmailForm] = useState<EmailForm>({
    email: "",
    phone: "",
    firstName: "",
    lastName: "",
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [validationResponse, setValidationResponse] =
    useState<ValidationResponse | null>(null);

  // Function for validating existing customer credentials using the API
  const validateCustomerCredentials = async (
    username: string,
    password: string
  ): Promise<boolean> => {
    try {
      const response: ValidationResponse = await validateOldCredentials(
        username,
        password
      );
      //console.log("API Response:", response);

      setValidationResponse(response);

      if (response.isValid) {
        // Since the API now returns username, password, and customerNo instead of user details,
        // we'll keep the form empty for the user to fill in their information
        setEmailForm({
          email: "",
          phone: "",
          firstName: "",
          lastName: "",
        });
        return true;
      } else {
        // Set specific error message from API
        if (response.errorMessage) {
          setErrors([response.errorMessage]);
        }
        return false;
      }
    } catch (error) {
      console.error("Error validating credentials:", error);
      setErrors([
        "An error occurred while validating your credentials. Please try again.",
      ]);
      return false;
    }
  };

  // Function for migrating account using the API
  const migrateAccount = async (
    email: string,
    phone: string,
    firstName: string,
    lastName: string
  ): Promise<boolean> => {
    if (!validationResponse) {
      setErrors(["Validation data is missing. Please start over."]);
      return false;
    }

    try {
      const response: MigrationResponse = await migrateFromOldWebshop(
        validationResponse.username || validationForm.username,
        validationResponse.password || validationForm.password,
        firstName,
        lastName,
        email,
        phone,
        validationResponse.customerNo || ""
      );

      //console.log("Migration API Response:", response);

      if (response.success) {
        return true;
      } else if (response.message) {
        setErrors([response.message]);
        return false;
      } else {
        setErrors(["An unexpected error occurred during migration."]);
        return false;
      }
    } catch (error) {
      console.error("Error migrating account:", error);
      setErrors([
        "An error occurred while creating your account. Please try again.",
      ]);
      return false;
    }
  };

  const handleValidationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setIsLoading(true);

    try {
      const isValid = await validateCustomerCredentials(
        validationForm.username,
        validationForm.password
      );

      if (isValid) {
        setCurrentStep(MigrationStep.EMAIL_ENTRY);
      } else {
        setErrors([
          "Invalid username or password. Please check your credentials and try again.",
        ]);
      }
    } catch (error) {
      setErrors([
        "An error occurred while validating your credentials. Please try again.",
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setIsLoading(true);

    try {
      const success = await migrateAccount(
        emailForm.email,
        emailForm.phone,
        emailForm.firstName,
        emailForm.lastName
      );

      if (success) {
        setCurrentStep(MigrationStep.SUCCESS);
        // Redirect to login page after 10 seconds
        setTimeout(() => {
          navigate("/");
        }, 10000);
      }
      // Error handling is done within migrateAccount function
    } catch (error) {
      setErrors([
        "An error occurred while creating your account. Please try again.",
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailFormChange = (
    e: React.ChangeEvent<HTMLInputElement> | CustomInputChangeEvent
  ) => {
    const { name, value } = e.target;
    setEmailForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const renderValidationStep = () => (
    <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold mb-6 text-gray-800">
        Step 1: Verify Your Existing Account
      </h3>
      <p className="text-gray-600 mb-6 text-sm">
        Please enter your current username and password to verify your existing
        account.
      </p>

      <form onSubmit={handleValidationSubmit} className="space-y-4">
        <StrucRow>
          <StrucCol size={12}>
            <InputWrapper>
              <InputLabel>Username</InputLabel>
              <CustomInput
                type="text"
                value={validationForm.username}
                onChange={(e) =>
                  setValidationForm({
                    ...validationForm,
                    username: e.target.value,
                  })
                }
                disabled={isLoading}
                required
              />
            </InputWrapper>
          </StrucCol>
        </StrucRow>

        <StrucRow>
          <StrucCol size={12}>
            <InputWrapper>
              <InputLabel>Password</InputLabel>
              <CustomInput
                type="password"
                value={validationForm.password}
                onChange={(e) =>
                  setValidationForm({
                    ...validationForm,
                    password: e.target.value,
                  })
                }
                disabled={isLoading}
                required
              />
            </InputWrapper>
          </StrucCol>
        </StrucRow>

        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            {errors.map((error, index) => (
              <p key={index} className="text-red-600 text-sm">
                {error}
              </p>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-4">
          <PrimaryButton
            type="submit"
            disabled={
              isLoading || !validationForm.username || !validationForm.password
            }
          >
            {isLoading ? "Validating..." : "Validate Account"}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );

  const renderEmailStep = () => (
    <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold mb-6 text-gray-800">
        Step 2: Create Your New Account
      </h3>
      <p className="text-gray-600 mb-6 text-sm">
        Great! Your credentials have been verified. Now please enter your
        personal information to create your new account.
      </p>

      <form onSubmit={handleEmailSubmit} className="space-y-4">
        <StrucRow className="flex justify-between">
          <StrucCol size={5.5}>
            <InputWrapper>
              <InputLabel>First Name</InputLabel>
              <CustomInput
                type="text"
                name="firstName"
                value={emailForm.firstName}
                onChange={handleEmailFormChange}
                disabled={isLoading}
                required
              />
            </InputWrapper>
          </StrucCol>
          <StrucCol size={5.5}>
            <InputWrapper>
              <InputLabel>Last Name</InputLabel>
              <CustomInput
                type="text"
                name="lastName"
                value={emailForm.lastName}
                onChange={handleEmailFormChange}
                disabled={isLoading}
                required
              />
            </InputWrapper>
          </StrucCol>
        </StrucRow>

        <StrucRow>
          <StrucCol size={12}>
            <InputWrapper>
              <InputLabel>Email Address</InputLabel>
              <CustomInput
                type="email"
                name="email"
                value={emailForm.email}
                onChange={handleEmailFormChange}
                disabled={isLoading}
                required
              />
            </InputWrapper>
          </StrucCol>
        </StrucRow>

        <StrucRow>
          <StrucCol size={12}>
            <InputWrapper>
              <InputLabel>Phone Number (include country code)</InputLabel>
              <CustomPhoneInput
                name="phone"
                value={emailForm.phone}
                onChange={handleEmailFormChange}
                disabled={isLoading}
              />
            </InputWrapper>
          </StrucCol>
        </StrucRow>

        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            {errors.map((error, index) => (
              <p key={index} className="text-red-600 text-sm">
                {error}
              </p>
            ))}
          </div>
        )}

        <div className="flex justify-between pt-4">
          <SecondaryButton
            type="button"
            onClick={() => setCurrentStep(MigrationStep.VALIDATION)}
            disabled={isLoading}
          >
            Back
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={
              isLoading ||
              !emailForm.email ||
              !emailForm.phone ||
              !emailForm.firstName ||
              !emailForm.lastName
            }
          >
            {isLoading ? "Creating Account..." : "Create Account"}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
      <div className="mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          Account created!
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          Check your email for further instructions.
        </p>
        <p className="text-gray-500 text-xs">Redirecting in 10 seconds...</p>
      </div>
    </div>
  );

  return (
    <div>
      <BoxContent className="pt-12 pb-32">
        <StrucRow>
          <StrucCol size={12}>
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <SectionHeaderTypography className="mb-4">
                  CREATE ACCOUNT FROM OLD LOGIN
                </SectionHeaderTypography>
              </div>

              {currentStep === MigrationStep.VALIDATION &&
                renderValidationStep()}
              {currentStep === MigrationStep.EMAIL_ENTRY && renderEmailStep()}
              {currentStep === MigrationStep.SUCCESS && renderSuccessStep()}
            </div>
          </StrucCol>
        </StrucRow>
      </BoxContent>
      <BoxFooter />
    </div>
  );
}
